import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import apiClient from '@skygroup/shared/API/apiClient';
import endpoints from '../utils/endpoints';
import {
  applyOrderProductsFromApi,
  AttachToOrderContext,
  clearAttachToOrderContext,
  mergeAttachOrderCart,
  setAttachOrderSnapshot,
  setAttachToOrderContext,
  setCartExchangeRates,
  setCommission,
  setCustomerInfo,
  setWholeCart,
} from '../modules/ShoppingCart/ShoppingCartSliceV2';
import { RootState, store } from '../store/store';

export const ATTACH_TO_ORDER_STORAGE_KEY = 'attach-to-order-context';
//per-tab flag: the agent has seen/confirmed the attach mode in this tab, so
//localStorage restores in the same tab don't re-prompt. Cleared together with
//the context so a brand-new attach session prompts again.
export const ATTACH_ACK_SESSION_KEY = 'attach-to-order-acknowledged';

//single place that forgets the persisted attach context (both storages) —
//used on cancel, on successful attach and on TTL/parse cleanup
export const clearAttachStorage = () => {
  localStorage.removeItem(ATTACH_TO_ORDER_STORAGE_KEY);
  sessionStorage.removeItem(ATTACH_ACK_SESSION_KEY);
};

const ATTACH_URL_PARAMS = [
  'attach_order_id',
  'attach_order_secret',
  'attach_mode',
  'replace_op_id',
  'replace_op_name',
];

/**
 * Consumes the "add/replace product on an existing order" context
 * that Backoffice passes to agent B2C via URL params
 * (?attach_order_id=&attach_order_secret=&attach_mode=add|replace&replace_op_id=).
 * Persists the context in localStorage so it survives navigation/reloads,
 * hydrates the ShoppingCartV2 with the WHOLE order (products + guests +
 * client, silently overwriting any previous cart — the agent's intent is
 * explicit), and exposes a cancel helper for when the agent abandons the
 * flow. The agent then adds the new product into this hydrated cart and
 * finalizes on the checkout page (see backoffice REPLACE_PRODUCT_DEEP_LINKS.md).
 */
const persistAttachContext = (dispatch: any, ctx: AttachToOrderContext) => {
  localStorage.setItem(
    ATTACH_TO_ORDER_STORAGE_KEY,
    JSON.stringify({ ...ctx, created_at: Date.now() })
  );
  dispatch(setAttachToOrderContext(ctx));
};

//one order fetch at a time — App-level consume and the checkout safety-net
//retry may race on mount; the GET is idempotent but there is no reason to
//fire it twice
let hydrateInFlightSecret: string | null = null;

//fetch the order and hydrate the V2 cart with it (same trio the ManageOrder
//page uses), plus the slice-level customer info
const hydrateOrderIntoCartImpl = (
  dispatch: any,
  ctx: AttachToOrderContext
) => {
  if (hydrateInFlightSecret === ctx.order_secret) {
    return;
  }
  hydrateInFlightSecret = ctx.order_secret;
  apiClient
    .get(
      `${endpoints.ORDER.GET}?order_secret=${ctx.order_secret}&skip_refresh_rates=1`
    )
    .then((res) => {
      const order = res?.data;
      if (!order || order.status === 'error' || !order.products) {
        console.warn('Attach-to-order: could not load the order');
        return;
      }

      //snapshot feeds mergeAttachOrderCart whenever a product-selection
      //flow resets the cart mid-journey
      dispatch(setAttachOrderSnapshot({ ...order, isSaved: true }));

      //The order's currency is fixed for the rest of this attach session: cart
      //totals are pinned to it and the agent is warned when they pick a product
      //priced in something else. Persisted like customer_name, from this fetch.
      const hydratedCtx: AttachToOrderContext = order.currency_sign
        ? { ...ctx, order_currency_sign: order.currency_sign }
        : ctx;
      if (hydratedCtx !== ctx) {
        //persist before the cart is rebuilt — calculateTotals reads the pinned
        //currency off the context
        persistAttachContext(dispatch, hydratedCtx);
      }

      //GET /order carries no exchange rates — they are a client-side
      //siteConfig snapshot. Without them the cart falls back to summing raw
      //amounts across currencies, so seed them before the totals are computed.
      const siteRates = store.getState().config?.config?.siteConfig?.rates;
      if (siteRates) {
        dispatch(setCartExchangeRates(siteRates));
      }

      const currentProducts =
        store.getState().shoppingCartV2.ShoppingCartV2?.products ?? [];
      const hasNewSelection = currentProducts.some(
        (p: any) => !p.id || p.subProducts?.some((sp: any) => !sp.id)
      );
      if (hasNewSelection) {
        //agent already picked a product while the order was loading —
        //merge the order around the selection instead of overwriting it
        dispatch(mergeAttachOrderCart());
      } else {
        dispatch(setWholeCart({ ...order, isSaved: true }));
        dispatch(applyOrderProductsFromApi(order.products));
      }
      if (order.agent_commission) {
        dispatch(setCommission(order.agent_commission));
      }

      const orderCustomer = order.customerInfo;
      if (orderCustomer) {
        const customerInfo = {
          first_name: orderCustomer.customer_first_name || '',
          last_name: orderCustomer.customer_last_name || '',
          phone: orderCustomer.customer_phone || '',
          email: orderCustomer.customer_email || '',
          comment: '',
          order_secret: '',
          customer_secret: orderCustomer.customer_secret || '',
        };
        dispatch(setCustomerInfo(customerInfo));
        localStorage.setItem('currentCustomer', JSON.stringify(customerInfo));
        persistAttachContext(dispatch, {
          ...hydratedCtx,
          customer_name:
            `${customerInfo.first_name} ${customerInfo.last_name}`.trim(),
        });
      }
    })
    .catch((err) => {
      console.warn('Failed to load the order to attach to', err);
    })
    .finally(() => {
      hydrateInFlightSecret = null;
    });
};

/** Checkout safety net: the App-level hook fetches the order once
 *  on landing, but that fetch can fail or the agent can land on checkout
 *  before it resolves. If attach mode is active and the order snapshot is
 *  still missing, retry the fetch so client info + existing guests are
 *  prefilled just like on the manage-order page. */
export const useEnsureAttachOrderHydrated = () => {
  const dispatch = useDispatch();
  const attachToOrder = useSelector(
    (state: RootState) => state.shoppingCartV2.attachToOrder
  );
  const hasSnapshot = useSelector(
    (state: RootState) =>
      !!state.shoppingCartV2.attachOrderSnapshot?.products?.length
  );

  useEffect(() => {
    if (attachToOrder && !hasSnapshot) {
      hydrateOrderIntoCartImpl(dispatch, attachToOrder);
    }
  }, [attachToOrder, hasSnapshot, dispatch]);
};

export const useLoadOrderAttachContext = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasProcessedRef = useRef(false);
  // When the mode is silently resumed from localStorage (reload /
  //new tab) the agent may have forgotten it — block the page with a
  //non-dismissable confirm until they explicitly continue or cancel the mode
  const [showAttachConfirm, setShowAttachConfirm] = useState(false);
  //context restored from localStorage on a silent resume, held pending until
  //the agent confirms the mode via the prompt. Nothing is applied to redux or
  //the cart while it sits here — see acknowledgeAttach (apply) / cancel (drop).
  const [pendingAttachContext, setPendingAttachContext] =
    useState<AttachToOrderContext | null>(null);
  const attachToOrder = useSelector(
    (state: RootState) => state.shoppingCartV2.attachToOrder
  );
  const cartProductsLength = useSelector(
    (state: RootState) => state.shoppingCartV2.ShoppingCartV2?.products?.length
  );

  //merge-on-selection: whenever the cart's product set changes while attach
  //mode is active (agent picked the replacement/new product — some selection
  //flows reset the cart in the process), restore the order around the new
  //selection. Idempotent no-op while the cart still holds the order.
  useEffect(() => {
    if (attachToOrder) {
      dispatch(mergeAttachOrderCart());
    }
  }, [attachToOrder, cartProductsLength, dispatch]);

  const cancelAttachToOrder = useCallback(() => {
    // Decline on the resume prompt: nothing was applied yet (no redux context,
    // no hydrated cart), so this must be a no-op on the regular UX — just
    // forget the persisted context and close. Deliberately NO clearShoppingCart:
    // the cart still holds the agent's own regular-mode state.
    clearAttachStorage();
    dispatch(clearAttachToOrderContext());
    // defensive: drop any residual attach params so a reload can't re-trigger
    // the mode (the resume path normally has none, but honor "discard anything
    // related to this mode")
    const params = new URLSearchParams(window.location.search);
    if (ATTACH_URL_PARAMS.some((p) => params.has(p))) {
      ATTACH_URL_PARAMS.forEach((p) => params.delete(p));
      const qs = params.toString();
      navigate(
        qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
        { replace: true }
      );
    }
    setPendingAttachContext(null);
    setShowAttachConfirm(false);
  }, [dispatch, navigate]);

  const acknowledgeAttach = useCallback(() => {
    // Continue: this is the ONLY place a silently-resumed mode is applied —
    // set the context (turns the mode on) and hydrate the order into the cart.
    if (pendingAttachContext) {
      dispatch(setAttachToOrderContext(pendingAttachContext));
      hydrateOrderIntoCartImpl(dispatch, pendingAttachContext);
    }
    sessionStorage.setItem(ATTACH_ACK_SESSION_KEY, '1');
    setPendingAttachContext(null);
    setShowAttachConfirm(false);
  }, [dispatch, pendingAttachContext]);

  const persistContext = useCallback(
    (ctx: AttachToOrderContext) => {
      persistAttachContext(dispatch, ctx);
    },
    [dispatch]
  );

  const hydrateOrderIntoCart = useCallback(
    (ctx: AttachToOrderContext) => {
      hydrateOrderIntoCartImpl(dispatch, ctx);
    },
    [dispatch]
  );

  useEffect(() => {
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    const searchParams = new URLSearchParams(window.location.search);
    const orderId = searchParams.get('attach_order_id');
    const orderSecret = searchParams.get('attach_order_secret');

    if (!orderId || !orderSecret) {
      // restore a context saved earlier in this browser (survives reloads).
      // Contexts older than 6h are treated as abandoned and discarded so a
      // forgotten flow can never hijack an unrelated cart days later.
      const ATTACH_CONTEXT_TTL_MS = 6 * 60 * 60 * 1000;
      try {
        const stored = localStorage.getItem(ATTACH_TO_ORDER_STORAGE_KEY);
        if (stored) {
          const { created_at, ...ctx } = JSON.parse(stored);
          if (created_at && Date.now() - created_at > ATTACH_CONTEXT_TTL_MS) {
            clearAttachStorage();
          } else if (sessionStorage.getItem(ATTACH_ACK_SESSION_KEY)) {
            // already confirmed in this tab — apply the mode immediately.
            // redux cart state does not survive a reload, so re-hydrate the
            // order to show the real order composition again.
            dispatch(setAttachToOrderContext(ctx));
            hydrateOrderIntoCart(ctx);
          } else {
            // silently resumed and NOT yet confirmed in this tab — apply
            // nothing (no redux context, no cart hydration) until the agent
            // explicitly continues. Hold the context pending and prompt; a
            // Cancel then leaves regular mode completely untouched.
            setPendingAttachContext(ctx);
            setShowAttachConfirm(true);
          }
        }
      } catch (e) {
        clearAttachStorage();
      }
      return;
    }

    const replaceOpId = searchParams.get('replace_op_id');
    const ctx: AttachToOrderContext = {
      order_id: Number(orderId),
      order_secret: orderSecret,
      mode:
        searchParams.get('attach_mode') === 'replace' || replaceOpId
          ? 'replace'
          : 'add',
      replaced_order_product_id: replaceOpId ? Number(replaceOpId) : undefined,
      replaced_product_name: searchParams.get('replace_op_name') || undefined,
    };
    persistContext(ctx);
    //deep-link entry is an intentional, just-clicked action — treat this tab
    //as already aware of the mode so later restores here don't prompt
    sessionStorage.setItem(ATTACH_ACK_SESSION_KEY, '1');

    // strip the consumed params from the URL (keep any others; `t`/`for` are
    // consumed and stripped by useLoadAgentUser). No client-side redirect —
    // Backoffice already opened the exact target page.
    const cleanParams = new URLSearchParams(searchParams);
    ATTACH_URL_PARAMS.forEach((p) => cleanParams.delete(p));
    const newUrl = cleanParams.toString()
      ? `${window.location.pathname}?${cleanParams.toString()}`
      : window.location.pathname;
    navigate(newUrl, { replace: true });

    hydrateOrderIntoCart(ctx);
  }, [dispatch, navigate, persistContext, hydrateOrderIntoCart]);

  return {
    attachToOrder,
    cancelAttachToOrder,
    showAttachConfirm,
    acknowledgeAttach,
    pendingAttachContext,
  };
};

export default useLoadOrderAttachContext;
